import { logger } from "@/utils/logger";

export type CreateSavePointSource =
  | "credit_purchase_redirect"
  | "auth_redirect"
  | "auto_save"
  | "before_unload"
  | "credit_modal_shown";

export interface CreateSavePointV1<T = any> {
  version: 1;
  source: CreateSavePointSource;
  savedAt: number;
  data: T;
  intentId?: string;
  /** Account that owned a paid-checkout restore when it was created. */
  ownerUserId?: string;
}

const LEGACY_KEYS = [
  // Saved prior to login to auto-generate on return
  "pendingDressForm",
  // Saved before credits checkout to restore after purchase
  "pendingDressFormBeforeCheckout",
];

const CANONICAL_KEY = "create.savePoint.v1";
const SCOPED_KEY_PREFIX = `${CANONICAL_KEY}.`;

function getSavePointKey(intentId?: string | null): string {
  return intentId ? `${SCOPED_KEY_PREFIX}${intentId}` : CANONICAL_KEY;
}

// Sources whose save point must survive leaving the tab. Email-confirmation
// links open a NEW tab (empty sessionStorage), and credit-purchase returns may
// too, so these are mirrored to localStorage (shared across same-origin tabs of
// the same browser). getCanonical() reads localStorage as a fallback and
// copies it into the current tab's sessionStorage. The shared copy is cleared
// only after a successful, exact restore so a wrong-account tab cannot strand
// another tab's handoff merely by inspecting it.
const DURABLE_SOURCES = new Set<CreateSavePointSource>([
  "auth_redirect",
  "credit_purchase_redirect",
]);

function safeParse<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export const SavePoint = {
  saveFormState<T = any>(
    data: T,
    source: CreateSavePointSource = "auto_save",
    intentId?: string | null,
    ownerUserId?: string | null
  ) {
    const payload: CreateSavePointV1<T> = {
      version: 1,
      source,
      savedAt: Date.now(),
      data,
      ...(intentId ? { intentId } : {}),
      ...(ownerUserId ? { ownerUserId } : {}),
    };
    try {
      const serialized = JSON.stringify(payload);
      const storageKey = getSavePointKey(intentId);
      if (typeof window !== "undefined" && window.sessionStorage) {
        sessionStorage.setItem(storageKey, serialized);
        logger.debug("SavePoint.saveFormState: Saved to sessionStorage", {
          component: "SavePoint",
          source,
          savedAt: payload.savedAt,
          hasData: !!data,
          dataKeys:
            data && typeof data === "object" ? Object.keys(data as object) : [],
          storageKey: CANONICAL_KEY,
        });
      }
      // Mirror durable sources to localStorage so a design started in one tab
      // survives an email-confirmation link that opens in a new tab.
      if (
        DURABLE_SOURCES.has(source) &&
        typeof window !== "undefined" &&
        window.localStorage
      ) {
        localStorage.setItem(storageKey, serialized);
      }
    } catch (error) {
      logger.error("SavePoint.saveFormState: Failed to save", {
        component: "SavePoint",
        source,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  },

  saveBeforeCheckout<T = any>(
    data: T,
    intentId?: string | null,
    ownerUserId?: string | null
  ) {
    this.saveFormState(data, "credit_purchase_redirect", intentId, ownerUserId);
  },

  saveOnAuthRedirect<T = any>(data: T, intentId?: string | null) {
    this.saveFormState(data, "auth_redirect", intentId);
  },

  getCanonical<T = any>(intentId?: string | null): CreateSavePointV1<T> | null {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return null;
    }

    // Try sessionStorage first (new location)
    const storageKey = getSavePointKey(intentId);
    const sessionData = sessionStorage.getItem(storageKey);
    if (sessionData) {
      const parsed = safeParse<CreateSavePointV1<T>>(sessionData);
      if (!intentId || parsed?.intentId === intentId) return parsed;
      return null;
    }

    // Fallback to localStorage for cross-tab durability. Copy rather than move:
    // callers still need to validate the opaque id, source, and owner before
    // they may consume the handoff.
    const localData = localStorage.getItem(storageKey);
    if (localData) {
      const parsed = safeParse<CreateSavePointV1<T>>(localData);
      if (parsed && (!intentId || parsed.intentId === intentId)) {
        // Copy to this tab's sessionStorage. SavePoint.clear() removes both
        // copies after the validated restore succeeds.
        try {
          sessionStorage.setItem(storageKey, localData);
        } catch {
          // If the copy fails, the durable shared value is still readable.
        }
        return parsed;
      }
    }

    return null;
  },

  getLatestSavePoint<T = any>(
    intentId?: string | null
  ): CreateSavePointV1<T> | null {
    // Try canonical first (sessionStorage, with localStorage fallback)
    const canonical = this.getCanonical<T>(intentId);
    if (canonical) return canonical;

    // A URL-bound return must fail closed instead of falling back to another
    // tab's singleton or legacy save point.
    if (intentId) return null;

    // Fallback to legacy keys for backward compatibility
    // Check sessionStorage first, then localStorage
    const storageTypes = [
      typeof window !== "undefined" && window.sessionStorage
        ? sessionStorage
        : null,
      typeof window !== "undefined" && window.localStorage
        ? localStorage
        : null,
    ].filter(Boolean) as Storage[];

    for (const storage of storageTypes) {
      for (const key of LEGACY_KEYS) {
        const legacy = safeParse<any>(storage.getItem(key));
        if (legacy) {
          // Convert legacy format to new format
          const converted: CreateSavePointV1<T> = {
            version: 1 as const,
            source: "auto_save" as CreateSavePointSource, // Default for legacy saves
            savedAt: legacy.savedAt || Date.now(),
            data: legacy.data || legacy,
          };

          // If found in localStorage, migrate to sessionStorage
          if (
            storage === localStorage &&
            typeof window !== "undefined" &&
            window.sessionStorage
          ) {
            try {
              sessionStorage.setItem(CANONICAL_KEY, JSON.stringify(converted));
              localStorage.removeItem(key);
            } catch {
              // If migration fails, just return the converted data
            }
          }

          return converted;
        }
      }
    }

    return null;
  },

  /**
   * Read an exact URL-scoped save point from durable, cross-tab storage only.
   * Auth confirmation links can open in a new tab, so a sessionStorage copy is
   * not sufficient proof that a handoff can safely leave the current page.
   */
  getDurableSavePoint<T = any>(
    intentId?: string | null
  ): CreateSavePointV1<T> | null {
    if (!intentId || typeof window === "undefined") {
      return null;
    }

    try {
      const durableStorage = window.localStorage;
      if (!durableStorage) return null;
      const parsed = safeParse<CreateSavePointV1<T>>(
        durableStorage.getItem(getSavePointKey(intentId))
      );
      return parsed?.intentId === intentId ? parsed : null;
    } catch {
      return null;
    }
  },

  hasAnySavePoint(): boolean {
    if (this.getCanonical()) return true;

    // Check legacy keys in both sessionStorage and localStorage
    const storageTypes = [
      typeof window !== "undefined" && window.sessionStorage
        ? sessionStorage
        : null,
      typeof window !== "undefined" && window.localStorage
        ? localStorage
        : null,
    ].filter(Boolean) as Storage[];

    for (const storage of storageTypes) {
      for (const key of LEGACY_KEYS) {
        if (storage.getItem(key)) return true;
      }
    }

    return false;
  },

  clear(intentId?: string | null, scope: "all" | "canonical" = "all") {
    const removeSavePoints = (storage: Storage) => {
      const removeItem = (key: string) => {
        try {
          storage.removeItem(key);
        } catch {
          // Continue clearing the other keys and storage boundary. A blocked
          // sessionStorage must not leave the durable bearer copy alive in
          // localStorage (or vice versa).
        }
      };

      try {
        if (scope === "canonical") {
          removeItem(CANONICAL_KEY);
          // These pre-v1 singleton keys are canonical drafts too. Preserve
          // only opaque URL-scoped v1 handoffs; otherwise an explicit fresh
          // canvas can immediately resurrect a legacy draft as newly saved.
          for (const key of LEGACY_KEYS) removeItem(key);
          return;
        }
        if (intentId) {
          removeItem(getSavePointKey(intentId));
          return;
        }
        const keys: string[] = [];
        for (let index = 0; index < storage.length; index++) {
          const key = storage.key(index);
          if (key === CANONICAL_KEY || key?.startsWith(SCOPED_KEY_PREFIX)) {
            keys.push(key);
          }
        }
        keys.forEach(removeItem);
        if (!intentId && scope === "all") {
          for (const key of LEGACY_KEYS) removeItem(key);
        }
      } catch {
        // Storage access can throw before individual removal (for example its
        // length/key getters). The other storage is still cleared below.
      }
    };

    if (typeof window === "undefined") return;

    // Isolate the two storage getters as well as their operations so a denied
    // sessionStorage boundary cannot skip durable localStorage cleanup.
    try {
      if (window.sessionStorage) {
        removeSavePoints(window.sessionStorage);
      }
    } catch {}
    try {
      if (window.localStorage) {
        removeSavePoints(window.localStorage);
      }
    } catch {}
  },

  restoreIfRequested<T = any>(
    onRestore: (data: T) => void,
    paramName = "restore_form"
  ) {
    try {
      const url = new URL(window.location.href);
      const shouldRestore = url.searchParams.get(paramName) === "true";
      if (!shouldRestore) return;

      // Try canonical first (sessionStorage, with localStorage fallback)
      const canonical = this.getCanonical<T>();
      if (canonical && canonical.data) {
        onRestore(canonical.data);
        this.clear();
        // Clean URL
        url.searchParams.delete(paramName);
        window.history.replaceState({}, "", url.toString());
        return;
      }

      // Fallback to legacy keys for backward compatibility
      const storageTypes = [
        typeof window !== "undefined" && window.sessionStorage
          ? sessionStorage
          : null,
        typeof window !== "undefined" && window.localStorage
          ? localStorage
          : null,
      ].filter(Boolean) as Storage[];

      for (const storage of storageTypes) {
        for (const key of LEGACY_KEYS) {
          const legacy = safeParse<any>(storage.getItem(key));
          if (legacy) {
            const data = legacy.data || legacy; // support plain legacy data
            onRestore(data as T);
            this.clear();
            url.searchParams.delete(paramName);
            window.history.replaceState({}, "", url.toString());
            return;
          }
        }
      }
    } catch {
      // swallow
    }
  },
};

export default SavePoint;
